import { X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";

interface SearchBarProps {
  onSearch?: (query: string) => void;
  onFocus?: () => void;
  onDownArrow?: () => void;
  initialValue?: string;
  debounceMs?: number;
}

export interface SearchBarRef {
  focus: () => void;
}

export const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(({ 
  onSearch, 
  onFocus,
  onDownArrow,
  initialValue = "",
  debounceMs = 500,
}, ref) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFocusedRef = useRef(false);

  // Expose focus method via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  // Debounced search
  const debouncedSearch = useCallback((searchValue: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      onSearch?.(searchValue);
    }, debounceMs);
  }, [onSearch, debounceMs]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Keep input in sync when URL-driven search state changes.
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    debouncedSearch(newValue);
  };

  const handleClear = () => {
    setValue("");
    // Clear immediately without debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearch?.("");
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Only trigger once per mount to avoid repeated fetches
    if (!hasFocusedRef.current) {
      hasFocusedRef.current = true;
      onFocus?.();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Escape to unfocus
    if (e.key === 'Escape') {
      e.preventDefault();
      inputRef.current?.blur();
    }
    // Down arrow to navigate to first item
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      inputRef.current?.blur();
      onDownArrow?.();
    }
  };

  const showKbdHint = !isFocused && !value;

  return (
    <div className="relative flex-1 min-w-[200px] max-w-md shadow-none">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
        <Search className="h-4 w-4 text-muted-foreground" />
      </div>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        placeholder="Search saved webpages..."
        className="w-full shadow-none pl-9 pr-8 rounded-sm bg-neutral-100 border border-neutral-200 hover:border-neutral-300 focus:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 text-neutral-800 placeholder:text-neutral-500 text-base lg:text-sm"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {showKbdHint && (
        <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 items-center justify-center rounded bg-white px-1.5 text-[10px] font-medium text-neutral-600 pointer-events-none">
          /
        </kbd>
      )}
      {value && (
        <button
          onClick={handleClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-neutral-400 hover:text-neutral-600 focus:outline-none"
        >
          <X className="h-4 w-4 text-primary" weight="bold" />
        </button>
      )}
    </div>
  );
});

SearchBar.displayName = "SearchBar";
